import { getProductAndPlanFromSku } from '@hotjar/product-catalog';
import type { ProductAllowance } from '@hotjar/subscription-management';
import {
  convertAllowancesToBasic,
  planIsNotBasic,
  removeAllowancesOnBasic,
} from '~/subscription-management/utils/allowance.utils';
import type { Context, NextEvent, UpdateAllowancesEvent } from '../../checkout.machine.types';

interface UpdatedProductAllowancesParams {
  allAllowances: ProductAllowance[];
  previouslyAddedAllowances: ProductAllowance[];
  toAddAllowances: ProductAllowance[];
  hasActiveSubscription: boolean;
}

interface GetIsChurningParams {
  allEffectiveAllowances: ProductAllowance[];
  allowancesToAdd: ProductAllowance[];
  allowancesToRemove: ProductAllowance[];
  hasActiveSubscription: boolean;
}

const isSameProductAndEntity = (initial: ProductAllowance, update: ProductAllowance) => {
  const { product: initialProduct } = getProductAndPlanFromSku(initial.sku);
  const { product: updateProduct } = getProductAndPlanFromSku(update.sku);

  return (
    initialProduct.id === updateProduct.id &&
    initial.entity.entityIdentifier === update.entity.entityIdentifier
  );
};

const isDifferentFromInitial = (
  current: ProductAllowance,
  allEffectiveAllowances: ProductAllowance[],
) => {
  const initial = allEffectiveAllowances.find((allowance) =>
    isSameProductAndEntity(allowance, current),
  );
  return initial?.sku !== current.sku;
};

const getIsChurning = ({
  allEffectiveAllowances,
  allowancesToAdd,
  allowancesToRemove,
  hasActiveSubscription,
}: GetIsChurningParams) => {
  const payableAddedAllowances = removeAllowancesOnBasic(allowancesToAdd);

  if (
    hasActiveSubscription &&
    payableAddedAllowances.length === 0 &&
    allowancesToRemove.length !== 0
  ) {
    // Go over all allowances before the update, and make sure they all basic, Or
    // discard the ones in the updatedAllowancesToRemove, as they are considered as basic
    return allEffectiveAllowances.every(
      (allowance) =>
        !planIsNotBasic(allowance) ||
        allowancesToRemove.some(
          (allowanceToRemove) =>
            allowanceToRemove.sku === allowance.sku &&
            allowanceToRemove.entity.entityIdentifier === allowance.entity.entityIdentifier,
        ),
    );
  }
  return false;
};

export const getUpdatedProductAllowances = ({
  allAllowances,
  previouslyAddedAllowances,
  toAddAllowances,
  hasActiveSubscription,
}: UpdatedProductAllowancesParams): Pick<
  Context,
  'allowancesToAdd' | 'allowancesToRemove' | 'isChurning'
> => {
  const allowancesToAdd = [...previouslyAddedAllowances];

  toAddAllowances.forEach((allowance) => {
    const index = allowancesToAdd.findIndex((initial) =>
      isSameProductAndEntity(initial, allowance),
    );

    const shouldAddNewEntry = index === -1;
    const canAdd = isDifferentFromInitial(allowance, allAllowances);

    if (shouldAddNewEntry && canAdd) {
      allowancesToAdd.push(allowance);
    }

    if (!shouldAddNewEntry && canAdd) {
      allowancesToAdd[index] = allowance;
    }

    if (!shouldAddNewEntry && !canAdd) {
      allowancesToAdd.splice(index, 1);
    }
  });

  /**
   * non-active subscriptions only pass the toAdd array, so we can always return an empty array in that case
   */
  const allowancesToRemove = hasActiveSubscription
    ? allAllowances.filter((allowance) =>
        allowancesToAdd.find((addedAllowance) => isSameProductAndEntity(addedAllowance, allowance)),
      )
    : [];

  return {
    allowancesToAdd,
    allowancesToRemove,
    isChurning: getIsChurning({
      allEffectiveAllowances: allAllowances,
      allowancesToAdd,
      allowancesToRemove,
      hasActiveSubscription,
    }),
  };
};

export const updateProductAllowances = (
  context: Context,
  event: UpdateAllowancesEvent,
): Partial<Context> => ({
  ...getUpdatedProductAllowances({
    allAllowances: context.allRenewedAllowances,
    previouslyAddedAllowances: context.allowancesToAdd,
    toAddAllowances: event.allowances,
    hasActiveSubscription: context.hasActiveSubscription,
  }),
});

export const resetProductAllowancesToInitial = (context: Context): Partial<Context> => ({
  ...getUpdatedProductAllowances({
    allAllowances: context.allEffectiveAllowances,
    previouslyAddedAllowances: [],
    toAddAllowances: [],
    hasActiveSubscription: context.hasActiveSubscription,
  }),
});

export const setProductAllowancesToBasic = (context: Context): Partial<Context> => ({
  ...getUpdatedProductAllowances({
    allAllowances: context.allEffectiveAllowances,
    previouslyAddedAllowances: [],
    toAddAllowances: convertAllowancesToBasic(context.allEffectiveAllowances),
    hasActiveSubscription: context.hasActiveSubscription,
  }),
});

export const updateInvoiceDetails = (
  { customer, canProceed }: Context,
  event: NextEvent,
): Pick<Context, 'canProceed' | 'customer'> => {
  if (event.formInputs !== undefined) {
    const { companyName, taxNumber, ...billingAddress } = event.formInputs;
    return {
      canProceed: false,
      customer: {
        ...customer,
        billingAddress: {
          ...customer.billingAddress,
          ...billingAddress,
        },
        companyName,
        taxNumber,
      },
    };
  }

  return { customer, canProceed };
};
